
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { toast } from './use-toast';
import { Property } from '../types';

interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  matches?: (Property & { match_score: number })[];
  isError?: boolean;
  isRetrying?: boolean;
}

interface FionaContext {
  hasProfile: boolean;
  preferencesSet: boolean;
  conversationLength: number;
}

export const useFionaChat = () => {
  const { user } = useAuth();
  
  const initialMessage: ChatMessage = {
    id: '1',
    content: "Hi! I'm Fiona, your AI rental concierge! 🦩 I'm here to help you find the perfect home. I can learn your preferences, show you matching properties, and guide you through the entire rental process. What would you like to know?",
    isUser: false,
    timestamp: new Date(),
  };

  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [isLoading, setIsLoading] = useState(false);
  const [context, setContext] = useState<FionaContext>({
    hasProfile: false,
    preferencesSet: false,
    conversationLength: 0
  });

  // Load previous chat history with timeout and error handling
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchHistory = async () => {
      if (!user) return;

      try {
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .limit(20)
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        if (error) {
          if (error.code !== '20') { // Ignore abort errors
            console.error('Error loading chat history:', error);
          }
          return;
        }

        if (!isMounted) return; // Don't update state if unmounted

        if (data && data.length > 0) {
          const history: ChatMessage[] = [];
          
          data.forEach((msg: any) => {
            history.push({
              id: `${msg.id}-user`,
              content: msg.message,
              isUser: true,
              timestamp: new Date(msg.created_at),
            });

            history.push({
              id: `${msg.id}-ai`,
              content: msg.response,
              isUser: false,
              timestamp: new Date(msg.created_at),
              matches: msg.match_data || undefined,
            });
          });

          setMessages([initialMessage, ...history]);
          setContext(prev => ({
            ...prev,
            conversationLength: history.length
          }));
        }
      } catch (error: any) {
        if (error?.code !== '20' && isMounted) {
          console.error('Error fetching chat history:', error);
        }
      }
    };

    fetchHistory();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [user?.id]); // Only depend on user.id to prevent re-fetches

  const sendMessage = useCallback(async (messageContent: string, retryMessageId?: string) => {
    if (!user || !messageContent.trim()) return;

    // If retrying, find and update the existing message
    if (retryMessageId) {
      setMessages(prev => prev.map(msg => 
        msg.id === retryMessageId 
          ? { ...msg, isRetrying: true, isError: false }
          : msg
      ));
    } else {
      // Add new user message
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content: messageContent.trim(),
        isUser: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
    }

    setIsLoading(true);

    try {
      // Call Fiona AI with clean message (context is handled in backend)
      const response = await supabase.functions.invoke('fiona-chat', {
        body: {
          message: messageContent.trim(),
          userId: user.id
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to get response from Fiona');
      }

      const { 
        response: aiResponse, 
        matches, 
        function_call, 
        user_context,
        fallback 
      } = response.data;

      // Update context
      if (user_context) {
        setContext(prev => ({
          ...prev,
          hasProfile: user_context.has_profile,
          preferencesSet: user_context.preferences_set,
          conversationLength: prev.conversationLength + 1
        }));
      }

      const aiMessage: ChatMessage = {
        id: retryMessageId ? `${retryMessageId}-retry` : (Date.now() + 1).toString(),
        content: aiResponse,
        isUser: false,
        timestamp: new Date(),
        matches: matches,
        isError: fallback || false
      };

      if (retryMessageId) {
        // Replace the retrying message
        setMessages(prev => prev.map(msg => 
          msg.id === retryMessageId 
            ? aiMessage
            : msg
        ));
      } else {
        setMessages(prev => [...prev, aiMessage]);
      }

      // Show appropriate toasts
      if (function_call) {
        if (function_call.name === 'update_preferences') {
          toast({
            title: "Preferences Updated! ✨",
            description: "I've learned something new about your preferences to find better matches.",
          });
        } else if (function_call.name === 'find_matching_properties') {
          toast({
            title: "Properties Found! 🏠",
            description: `I found ${matches?.length || 0} properties that match your criteria.`,
          });
        }
      }

      // Smart follow-up suggestions
      if (matches && matches.length > 0 && context.conversationLength < 3) {
        setTimeout(() => {
          const followUpMessage: ChatMessage = {
            id: (Date.now() + 2).toString(),
            content: "Would you like me to help you schedule tours for any of these properties? I can also help you prepare rental applications or find more options in specific neighborhoods! 🏡",
            isUser: false,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, followUpMessage]);
        }, 2000);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage: ChatMessage = {
        id: retryMessageId || (Date.now() + 1).toString(),
        content: "I apologize, but I'm having trouble connecting right now. Please try again - I'll remember our conversation! 🦩",
        isUser: false,
        timestamp: new Date(),
        isError: true
      };

      if (retryMessageId) {
        setMessages(prev => prev.map(msg => 
          msg.id === retryMessageId 
            ? errorMessage
            : msg
        ));
      } else {
        setMessages(prev => [...prev, errorMessage]);
      }

      toast({
        title: "Connection Error",
        description: "Fiona is temporarily unavailable. Your conversation is saved.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, context.conversationLength]);

  const retryMessage = useCallback(async (messageId: string, originalContent: string) => {
    await sendMessage(originalContent, messageId);
  }, [sendMessage]);

  const clearChat = useCallback(async () => {
    setMessages([initialMessage]);
    setContext({
      hasProfile: false,
      preferencesSet: false,
      conversationLength: 0
    });
    
    if (user) {
      try {
        await supabase.from('chat_messages').delete().eq('user_id', user.id);
        toast({
          title: "Chat Cleared",
          description: "Your conversation with Fiona has been reset.",
        });
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
  }, [user]);

  return {
    messages,
    isLoading,
    sendMessage,
    retryMessage,
    clearChat,
    context
  };
};
